// @flow
import { useState } from 'react';
import {
  Box, Button, Dialog, DialogContent, DialogTitle, MenuItem, Select, Stack, TextField, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ModalCloseButton } from '@coverright/ui/shared';
import { enqueueSnackbar } from 'notistack';
import { AiBadge, useEditQaReviewMutation } from '../../../../../../shared';
import { CallQa, QaAnswer } from './call-qa-types';

const ANSWERS: QaAnswer[] = ['Yes', 'No', 'Not Stated', 'N/A'];

type Props = {
  open: boolean;
  onClose: () => void;
  callQa: CallQa;
  airCallId?: string | number | null;
};

export const QaEditModal = ({ open, onClose, callQa, airCallId }: Props) => {
  const [overriddenScore, setOverriddenScore] = useState<string>(
    callQa.overriddenScore != null ? String(callQa.overriddenScore) : String(callQa.qaScore ?? '')
  );
  const [answers, setAnswers] = useState<Record<string, string>>(
    () => Object.fromEntries(callQa.questions.map(q => [q.id, q.answer]))
  );
  const [note, setNote] = useState(callQa.reviewNote || '');

  const [save, { loading }] = useEditQaReviewMutation({
    onCompleted: () => {
      enqueueSnackbar('QA review saved!', { variant: 'success' });
      onClose();
    },
    // Re-fetch the call so the drawer re-renders from the persisted effectiveScore / questions.
    refetchQueries: ['callHistoryItems', 'callHistoryByAirCallId'],
  });

  const handleSave = () => {
    if (airCallId == null) return;
    const scoreNum = overriddenScore === '' ? null : Math.max(0, Math.min(100, parseInt(overriddenScore, 10)));
    // Backend deserializes `editedAuditSheet` into an `AuditSheet` object via Jackson,
    // which expects SNAKE_CASE keys (max_deduction, deduction_applied, source_air_call_id) -
    // note the asymmetry: the GraphQL output (callQa.questions) is camelCase, but this
    // input wire shape is snake_case. Wrap under `{ questions: [...] }`.
    const questions = callQa.questions.map(q => ({
      id: q.id,
      category: q.category,
      question: q.question,
      answer: answers[q.id] ?? q.answer,
      evidence: q.evidence ?? null,
      source_air_call_id: q.sourceAirCallId ?? null,
      max_deduction: q.maxDeduction,
      deduction_applied: q.deductionApplied,
    }));
    const editedAuditSheet = JSON.stringify({ questions });
    save({
      variables: {
        airCallId,
        overriddenScore: scoreNum,
        editedAuditSheet,
        note: note || null,
      },
    });
  };

  return (
    <Dialog fullWidth maxWidth={'sm'} open={open} onClose={onClose}>
      <DialogTitle variant={'h3'}>
        Edit QA review
        <ModalCloseButton onClick={onClose} sx={{ position: 'absolute', top: 4, right: 8 }} />
      </DialogTitle>
      <DialogContent>
        <Stack gap={2.5}>
          <Stack direction={'row'} alignItems={'center'} gap={1}>
            <AiBadge label={`AI Scored ${callQa.qaScore}%`} />
            <Typography variant={'caption'} color={'text.secondary'}>
              AI original is never destroyed - your edit is stored as an override.
            </Typography>
          </Stack>

          {airCallId == null && (
            <Box sx={{ bgcolor: '#FDECEA', border: '1px solid #F14E42', borderRadius: 1, p: 1.5 }}>
              <Typography variant={'caption'} sx={{ color: '#B42318' }}>
                This call has no Aircall ID, so the QA review cannot be saved. (Backend exposes the ID only via
                the call lookup, not on the call object - flag to backend if editing from here is required.)
              </Typography>
            </Box>
          )}

          <Box>
            <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
              Override score
            </Typography>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <TextField
                size={'small'}
                type={'number'}
                value={overriddenScore}
                onChange={e => setOverriddenScore(e.target.value)}
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 110 }}
              />
              <Typography variant={'caption'} color={'text.secondary'}>0-100</Typography>
            </Stack>
          </Box>

          <Box>
            <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
              Edit audit sheet
            </Typography>
            <Stack gap={0.5} sx={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1, p: 1 }}>
              {callQa.questions.map(q => (
                <Stack key={q.id} direction={'row'} alignItems={'center'} gap={1} sx={{ py: 0.5 }}>
                  <Select
                    size={'small'}
                    value={answers[q.id] ?? q.answer}
                    onChange={e => setAnswers(prev => ({ ...prev, [q.id]: e.target.value as QaAnswer }))}
                    sx={{ width: 130, flexShrink: 0 }}
                  >
                    {ANSWERS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                  <Typography variant={'body2'}>
                    <Box component={'span'} sx={{ color: '#9CA3AF', fontWeight: 600, mr: 0.75 }}>{q.id}</Box>
                    {q.question}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>
              Review note
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </Box>

          <Stack direction={'row'} gap={2} justifyContent={'flex-end'}>
            <Button variant={'outlined'} onClick={onClose}>Cancel</Button>
            <LoadingButton loading={loading} disabled={airCallId == null} variant={'contained'} onClick={handleSave}>Save</LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};